import type { VerlaufEintrag } from "../v3/verlauf";

/**
 * WELCHE PUNKTE das Rechtsklick-Menü einer Ereigniskarte trägt — und in
 * welcher Reihenfolge (PO 16.09.2026).
 *
 * REINE Ableitung, ohne React und ohne Fetch (Muster {@link ./ereignisAktionen}
 * und `blockZuweisung.ts`): die Ordnung ist eine fachliche Entscheidung mit
 * einer Tabelle dahinter, keine Render-Angelegenheit. Als Objektliteral mitten
 * im JSX ließe sie sich nur über die gerenderte Seite prüfen; hier steht sie an
 * einem Ort mit einem Test daneben.
 *
 * ══ DIE ORDNUNG (PO 16.09.2026) ════════════════════════════════════════════
 * | Mail     | Anhänge übernehmen · Als Anhang an neue Mail · — · Zuweisen-Gruppe · — · Entfernen (rot) |
 * | Dokument | Umbenennen… · — · Zuweisen-Gruppe · — · Entfernen · Löschen (rot) |
 * | Anruf    | Zuweisen-Gruppe · — · Zusätzlich-Gruppe · Weiterem Vorgang… · — · Entfernen (rot) |
 * | Termin   | Zuweisen-Gruppe · — · Entfernen (rot)                          |
 *
 * Oben steht, was mit dem Eintrag selbst geschieht, in der Mitte die
 * Einsortierung, unten in Rot das Wegnehmen — der harmlose Weg zuerst, der
 * unumkehrbare zuletzt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ WARUM ES KEIN „LÖSCHEN“ AN MAIL, ANRUF UND TERMIN GIBT ═════════════════
 * Der Vorgang hat von diesen dreien nur die VERKNÜPFUNG: die Mail liegt in
 * Graph, der Anruf in der Telefonanlage, der Termin im Kalender. „Aus dem
 * Vorgang entfernen“ löst bei der Mail den Link sogar hart (E3 vom 16.09.2026:
 * die abgelegte `.eml` und übernommene Anhänge bleiben liegen) — ein zweiter,
 * schärferer Punkt daneben hätte kein Ziel mehr. Nur das Dokument gehört KMVS
 * selbst, und nur dort steht deshalb „Löschen“.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ ANRUFE: VERSCHIEBEN UND ZUSÄTZLICH (PO 01.10.2026) ═════════════════════
 * „Ein Anruf kann man mehreren Vorgängen (und mehreren To-dos innerhalb eines
 * Vorgangs) zuweisen." Beim Anruf heißt „Anderem To-do zuweisen" deshalb
 * VERSCHIEBEN — diese eine Zuordnung wandert. Darunter „Zusätzlich zuordnen"
 * (der Anruf bleibt, wo er ist, und hängt außerdem am gewählten To-do) und
 * „Weiterem Vorgang zuordnen…" (der Telefonie-Dialog). To-dos, an denen der
 * Anruf schon hängt, fallen aus beiden Gruppen heraus.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ „→ ZURÜCK AN DEN VORGANG“ IST WEG (A4 vom 16.09.2026) ══════════════════
 * Seit der Vorgang mit dem ersten To-do selbst zum ersten To-do wird, gibt es
 * keinen Strang „am Vorgang“ mehr: ein dorthin zurückgehängter Eintrag wäre in
 * keiner Karte mehr sichtbar. Ohne To-dos hing der Punkt ohnehin nie im Menü
 * (er erschien nur mit `todoId != null`), es geht also nichts verloren.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Was ein Menüpunkt auslöst. `null` steht an der Überschrift. */
export type EreignisMenueAktion =
    | { art: "anhaenge" }
    | { art: "alsAnhang" }
    | { art: "umbenennen" }
    | { art: "zuweisen"; todoId: number }
    | { art: "zusaetzlich"; todoId: number }
    | { art: "weitererVorgang" }
    | { art: "neuesTodo" }
    | { art: "entfernen" }
    | { art: "loeschen" };

export interface EreignisMenueEintrag {
    label: string;
    /** `null` = nicht klickbare Überschrift über der Zuweisen-Gruppe. */
    aktion: EreignisMenueAktion | null;
    /** Haarlinie ÜBER diesem Punkt. */
    trennerDavor?: boolean;
    /** Rot — die wegnehmenden Punkte. */
    gefahr?: boolean;
}

/** Mail → neue Mail mit ihr als Anhang (User 22.10.2026). */
export const ALS_ANHANG_LABEL = "Als Anhang an neue Mail";

/** Die Überschrift der Zuweisen-Gruppe (PO 16.09.2026). */
export const ZUWEISEN_UEBERSCHRIFT = "Anderem To-do zuweisen";

/** Anruf: die Gruppe „auch dort" (PO 01.10.2026). */
export const ZUSAETZLICH_UEBERSCHRIFT = "Zusätzlich zuordnen";

type TodoZiel = { id: number; titel?: string | null };

/**
 * Die Menüpunkte für EINE Ereigniskarte.
 *
 * Die Art wird an der TRAGENDEN LAST abgelesen, nicht an `eintrag.art`: eine
 * abgelegte `.eml` steht mit `art: "mail"` im Strom, ist aber ein Dokument
 * (`baueVerlauf`) — sie lässt sich umbenennen und löschen, hat aber keine
 * Graph-Anhänge, die man übernehmen könnte. `eintrag.mail` trennt beides.
 */
export function ereignisMenuePunkte(
    eintrag: Pick<VerlaufEintrag, "todoId" | "mail" | "dokument" | "anruf" | "termin">,
    todos: ReadonlyArray<TodoZiel>,
    /**
     * Anruf: To-dos DIESES Vorgangs, an denen er schon hängt (alle seine
     * Zuordnungen hier). Sie stehen in keiner der beiden Gruppen.
     */
    anrufTodoIds?: ReadonlyArray<number>,
): EreignisMenueEintrag[] {
    const punkte: EreignisMenueEintrag[] = [];
    const istGraphMail = eintrag.mail != null;
    const dok = istGraphMail ? undefined : eintrag.dokument;

    if (istGraphMail) {
        punkte.push({ label: "Anhänge übernehmen", aktion: { art: "anhaenge" } });
        // Wie Outlooks „Als Anlage weiterleiten" (User 22.10.2026): neue Mail
        // mit dieser Mail als `.eml` im Anhang.
        punkte.push({ label: ALS_ANHANG_LABEL, aktion: { art: "alsAnhang" } });
    } else if (dok) {
        punkte.push({ label: "Umbenennen…", aktion: { art: "umbenennen" } });
    }

    // Die Gruppe trennt sich nur ab, wenn etwas VOR ihr steht — ein Strich am
    // oberen Menürand wäre eine Linie ohne Aussage.
    const trennerVorGruppe = punkte.length > 0;
    // Das To-do, an dem der Eintrag schon hängt, fällt heraus: es anzubieten
    // hieße, eine Aktion ohne Wirkung anzuzeigen.
    const schonDran = new Set<number>(anrufTodoIds ?? []);
    const ziele = todos.filter((t) => t.id !== eintrag.todoId && !(eintrag.anruf && schonDran.has(t.id)));

    if (ziele.length > 0) {
        // Ohne Überschrift stünden die To-do-Titel nackt zwischen den Befehlen
        // und läsen sich wie weitere Aktionen (PO 16.09.2026).
        punkte.push({
            label: ZUWEISEN_UEBERSCHRIFT,
            aktion: null,
            ...(trennerVorGruppe ? { trennerDavor: true } : {}),
        });
        for (const t of ziele) {
            punkte.push({
                label: `→ ${t.titel?.trim() || `To-do ${t.id}`}`,
                aktion: { art: "zuweisen", todoId: t.id },
            });
        }
        punkte.push({ label: "Neues To-do…", aktion: { art: "neuesTodo" } });
    } else {
        // Gibt es kein anderes To-do, wäre „Anderem To-do zuweisen“ über einem
        // einzigen „Neues To-do…“ eine Überschrift über nichts.
        punkte.push({
            label: "Neues To-do…",
            aktion: { art: "neuesTodo" },
            ...(trennerVorGruppe ? { trennerDavor: true } : {}),
        });
    }

    // Anruf (PO 01.10.2026): zusätzlich an weitere To-dos dieses Vorgangs und
    // an weitere Vorgänge — die bisherige Zuordnung bleibt dabei stehen.
    if (eintrag.anruf) {
        if (ziele.length > 0) {
            punkte.push({ label: ZUSAETZLICH_UEBERSCHRIFT, aktion: null, trennerDavor: true });
            for (const t of ziele) {
                punkte.push({
                    label: `+ ${t.titel?.trim() || `To-do ${t.id}`}`,
                    aktion: { art: "zusaetzlich", todoId: t.id },
                });
            }
        }
        punkte.push({
            label: "Weiterem Vorgang zuordnen…",
            aktion: { art: "weitererVorgang" },
            ...(ziele.length === 0 ? { trennerDavor: true } : {}),
        });
    }

    /*
     * „Löschen“ steht NUR am Dokument — und nicht an einem Dokument, das an
     * einem Termin hängt: dessen Anhänge werden am Termin verwaltet, hier wäre
     * es fremdes Aufräumen (Muster aus `v3/AnhangLeiste.tsx`).
     */
    const zeigeLoeschen = dok != null && dok.calendarTerminId == null;

    punkte.push({
        label: "Aus dem Vorgang entfernen",
        aktion: { art: "entfernen" },
        trennerDavor: true,
        // Rot ist immer der UNTERSTE Punkt: steht „Löschen“ darunter, trägt
        // es die Farbe, sonst das Entfernen. Zwei rote Zeilen nebeneinander
        // nähmen der Farbe ihre Warnung.
        gefahr: !zeigeLoeschen,
    });

    if (zeigeLoeschen) {
        punkte.push({ label: "Löschen", aktion: { art: "loeschen" }, gefahr: true });
    }

    return punkte;
}
